import { Routes } from '@angular/router';

import { LoginGuard } from './loginguard';
import { LunchGuard } from './lunchguard';
import { LunchTimerComponent, NonProdListComponent, ReimbursementListComponent, SpecialHoursListComponent } from '@ftr/owa-timekeeping';
import { CoreGuardService } from '@ftr/owa-core';
import { ConsolidateNotesComponent, SparkSearchFormComponent } from '@ftr/owa-jobs';
import { ProfileResolver } from './profile.resolver';
import { ServicemanComponent } from '@ftr/owa-facilities';
import { LegacyMtpnTimeRedirectComponent } from './legacy-mtpn-time-redirect.component';
import { LegacyMtpnTimeRedirectGuard } from './legacy-mtpn-time-redirect.guard';

const timeRouteChildren: Routes = [
    {
        path: '',
        loadComponent: () =>
            import('./time/mytime-wrapper/mytime-wrapper.component')
                .then(m => m.MytimeWrapperComponent)
    },
    {
        path: 'timecard/:timeCardId',
        loadComponent: () =>
            import('./time/detail/detail.component').then(m => m.DetailComponent)
    },
    { path: 'nonprods/:date', component: NonProdListComponent
     },
    { path: 'specialhours/:date', component: SpecialHoursListComponent },
    { path: 'reimbursements/:date', component: ReimbursementListComponent },
    {
        path: 'adjustment',
        loadComponent: () =>
            import('./time/adjustments-wrapper/adjustments-wrapper.component')
                .then(m => m.AdjustmentsWrapperComponent)
               
    },
    {
        path: 'adjustment/:date/:taskSeq',
        loadComponent: () =>
            import('./time/adjustments-timeblock-wrapper/adjustments-timeblock-wrapper.component')
                .then(m => m.AdjustmentsTimeblockWrapperComponent)
                
    }
];

const omegaToolsRouteChildren: Routes = [
    {
        path: '',
        loadComponent: () =>
            import('./tools/tools.component').then(m => m.ToolsComponent)
    },
    {
        path: 'schedule',
        loadComponent: () =>
            import('./tools/schedule/schedule.component').then(m => m.ScheduleComponent)
    },
    {
        path: 'mytimeschedule',
        loadComponent: () =>
            import('./tools/mytimeschedule/mytimeschedule.component').then(m => m.MytimescheduleComponent)
    },
    {
        path: 'mySchedule',
        loadComponent: () =>
            import('./tools/my-schedule/my-schedule.component').then(m => m.MyScheduleComponent)
    },
    {
        path: 'racksheets',
        loadComponent: () =>
            import('./tools/racksheets/racksheets.component').then(m => m.RacksheetsComponent)
    },
    {
        path: 'racksheets/details/:env/:orderNumber/:tn/:officeCode/:status',
        loadComponent: () =>
            import('./tools/racksheets/rack-sheet-details/rack-sheet-details.component')
                .then(m => m.RackSheetDetailsAppComponent)
    },
    { path: 'serviceman', component: ServicemanComponent },
    {
        path: 'tourTrades',
        loadComponent: () =>
            import('./tools/tour-trades/tour-trades.component').then(m => m.TourTradesComponent)
    }
];

const messagesRouteChildren: Routes = [
    {
        path: '',
        loadComponent: () =>
            import('./messages/messages.component').then(m => m.MessagesComponent)
    },
    {
        path: 'boldchat',
        loadComponent: () =>
            import('./boldchat/boldchat/boldchat.component').then(m => m.BoldchatComponent)
    },
    {
        path: 'chatlist',
        loadComponent: () =>
            import('./messages/chatlistwrapper/chatlistwrapper.component')
                .then(m => m.ChatlistwrapperComponent)
    },
    {
        path: 'notifications',
        loadComponent: () =>
            import('./messages/notifications-wrapper/notifications-wrapper.component')
                .then(m => m.NotificationsWrapperComponent)
    },
    {
        path: 'message',
        loadComponent: () =>
            import('./messages/message.component/message.component')
                .then(m => m.MessageComponent)
    }
];

const omegaRouteChildren: Routes = [
    {
        path: 'timekeeping',
        children: timeRouteChildren
    },
    {
        path: 'messages',
        children: messagesRouteChildren
    },
    {
        path: 'tools',
        children: omegaToolsRouteChildren
    }
];

export const appRoutes: Routes = [
    {
        path: 'time',
        canActivate: [LoginGuard],
        data: { role: 'CONTRACTOR' },
        children: timeRouteChildren
    },
    {
        path: 'omega',
        canActivate: [LoginGuard],
        data: { role: 'CONTRACTOR', omegaMode: true },
        children: omegaRouteChildren
    },
    {
        path: 'mtpn-time',
        canActivate: [LegacyMtpnTimeRedirectGuard],
        children: [
            { path: '', component: LegacyMtpnTimeRedirectComponent, pathMatch: 'full' },
            { path: '**', component: LegacyMtpnTimeRedirectComponent }
        ]
    },
    {
        path: 'home',
        canActivate: [LoginGuard],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./home/home.component').then(m => m.HomeComponent),
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('./home/dashboard/dashboard.component')
                                .then(m => m.DashboardComponent),
                        resolve: { isProfileResolved: ProfileResolver }
                    },
                    {
                        path: 'job/:jobId',
                        loadComponent: () =>
                            import('./home/jobs/detail-wrapper/detail-wrapper.component')
                                .then(m => m.DetailWrapperComponent)
                    },
                    {
                        path: 'rackSheets/:jobId',
                        loadComponent: () =>
                            import('./home/jobs/racksheet-list/racksheet-list.component')
                                .then(m => m.RacksheetListComponent),
                        children: [
                            {
                                path: '',
                                loadComponent: () =>
                                    import('./home/jobs/rack-sheets/rack-sheets.component')
                                        .then(m => m.RackSheetsComponent)
                            },
                            {
                                path: 'details/:env/:orderNumber/:tn/:officeCode/:status',
                                loadComponent: () =>
                                    import('./home/jobs/rack-sheet-details/rack-sheet-details.component')
                                        .then(m => m.RackSheetDetailsAppComponent)
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        path: 'dropship/:dropshipUsi',
        canActivate: [LoginGuard], // Add guard if needed
        loadComponent: () =>
            import('./tools/dropship-wrapper/dropship-wrapper.component')
                .then(m => m.DropshipWrapperComponent)
    },
    {
        path: 'messages',
        canActivate: [LoginGuard],
        children: messagesRouteChildren
    },
    {
        path: 'tools',
        canActivate: [LoginGuard],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./tools/tools.component').then(m => m.ToolsComponent)
            },
            {
                path: 'schedule',
                loadComponent: () =>
                    import('./tools/schedule/schedule.component').then(m => m.ScheduleComponent)
            },
            {
                path: 'mytimeschedule',
                loadComponent: () =>
                    import('./tools/mytimeschedule/mytimeschedule.component').then(m => m.MytimescheduleComponent)
            },
            {
                path: 'myTeam',
                loadComponent: () =>
                    import('./tools/my-team/my-team.component').then(m => m.MyTeamComponent)
            },
            {
                path: 'flexmaps',
                loadComponent: () =>
                    import('./tools/flexmaps-wrapper/flexmaps-wrapper.component')
                        .then(m => m.FlexmapsWrapperComponent)
            },
            {
                path: 'diagnostics',
                loadComponent: () =>
                    import('./tools/facility-diagnostics/facility-diagnostics.component')
                        .then(m => m.FacilityDiagnosticsComponent)
            },
            {
                path: 'forms',
                loadComponent: () =>
                    import('./tools/adhoc-forms/adhoc-forms.component').then(m => m.AdhocFormsComponent)
            },
            { path: 'notes', component: ConsolidateNotesComponent },
            {
                path: 'swcForm',
                loadComponent: () =>
                    import('./tools/swc-drop-action-form/swc-drop-action-form.component')
                        .then(m => m.SwcDropActionFormComponent)
            },
            {
                path: 'mySchedule',
                loadComponent: () =>
                    import('./tools/my-schedule/my-schedule.component').then(m => m.MyScheduleComponent)
            },
            {
                path: 'wireDiagrams',
                loadComponent: () =>
                    import('./tools/wire-diagrams/wire-diagrams.component').then(m => m.WireDiagramsComponent)
            },
            {
                path: 'racksheets',
                loadComponent: () =>
                    import('./tools/racksheets/racksheets.component').then(m => m.RacksheetsComponent)
            },
            {
                path: 'routines',
                loadComponent: () =>
                    import('./tools/routines/routines.component').then(m => m.RoutinesComponent)
            },
            {
                path: 'racksheets/details/:env/:orderNumber/:tn/:officeCode/:status',
                loadComponent: () =>
                    import('./tools/racksheets/rack-sheet-details/rack-sheet-details.component')
                        .then(m => m.RackSheetDetailsAppComponent)
            },
            { path: 'servcieMan', redirectTo: 'serviceman', pathMatch: 'full' },
            { path: 'serviceman', component: ServicemanComponent },
            {
                path: 'inventory',
                loadComponent: () =>
                    import('./tools/inventory/inventory.component').then(m => m.InventoryComponent)
            },
            {
                path: 'customerSearch',
                loadComponent: () =>
                    import('./tools/customer-search/customer-search.component')
                        .then(m => m.CustomerSearchComponent)
            },
            {
                path: 'telematics',
                loadComponent: () =>
                    import('./tools/telematics/telematics.component').then(m => m.TelematicsViewComponent)
            },
            { path: 'spark', component: SparkSearchFormComponent },
            {
                path: 'triadQueue',
                loadComponent: () =>
                    import('./tools/triad-queue/triad-queue.component').then(m => m.TriadQueueViewComponent)
            },
            {
                path: 'tourTrades',
                loadComponent: () =>
                    import('./tools/tour-trades/tour-trades.component').then(m => m.TourTradesComponent)
            }
        ]
    },
    {
        path: 'profile',
        canActivate: [LoginGuard],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('./profile/profile.component').then(m => m.ProfileComponent)
            }
        ]
    },
    {
        path: 'oauth-popup',
        loadComponent: () =>
            import('./oauth-popup/oauth-popup.component').then(m => m.OauthPopupComponent)
    },
    {
        path: 'login',
        redirectTo: '/',
        pathMatch: 'full'
    },
    {
        path: '',
        loadComponent: () => import('./login/login.component').then(m => m.LoginComponent)
    },
    {
        path: 'timer',
        component: LunchTimerComponent,
        canDeactivate: [LunchGuard]
    },
    {
        path: 'pendingqueue',
        loadComponent: () => import('./pending-queue/pending-queue.component').then(m => m.PendingQueueComponent),
        canDeactivate: [CoreGuardService]
    },
    {
        path: 'specialhours/:date',
        component: SpecialHoursListComponent
    },
    {
        path: 'reimbursements/:date',
        component: ReimbursementListComponent
    }
];
